import { useState } from "react";
import { IOrders, OrderItem } from "../models/IOrders";
import StandardModal from "./StandardModal";
import OrderReport from "./OrderReport";
import { formatDateToDayMonthYear, orderStatuses } from "../utils/Util";

type TabId = 'active' | 'delivered' | 'cancelled' | 'all' | 'report';

const STATUS_COLORS: Record<number, { bg: string; color: string; border: string }> = {
  0: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  1: { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  2: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  3: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  4: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

const TASK_ICON: Record<number, string> = {
  0: 'bi-bag-check',
  1: 'bi-check-circle-fill',
  2: 'bi-fire',
  3: 'bi-bicycle',
  4: 'bi-x-circle-fill',
};

function getTaskFromOrder(item: OrderItem): number {
  const status = orderStatuses[item.orderStatusId];
  return status?.task ?? -1;
}

function OrderCard({ item, onClick }: { item: OrderItem; onClick: () => void }) {
  const task = getTaskFromOrder(item);
  const style = STATUS_COLORS[task] ?? STATUS_COLORS[0];
  const icon = TASK_ICON[task] ?? 'bi-circle';

  return (
    <div
      className="col-12 col-md-6 col-xl-4 mb-3"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div
        className="rounded-3 p-3 h-100"
        style={{
          border: `1.5px solid ${style.border}`,
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 18px rgba(0,0,0,0.12)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
        }}
      >
        {/* Üst satır: sipariş no + durum badge */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <div className="fw-bold" style={{ color: '#111827', fontSize: '0.88rem' }}>
              <i className="bi bi-hash text-muted"></i>{item.orderNameUniqe}
            </div>
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
              <i className="bi bi-calendar3 me-1"></i>{formatDateToDayMonthYear(item.orderDate)}
            </div>
          </div>
          <span
            className="badge d-flex align-items-center gap-1"
            style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}`, borderRadius: '20px', fontSize: '0.73rem', padding: '4px 10px', fontWeight: 600 }}
          >
            <i className={`bi ${icon}`}></i>
            {item.orderStatus.orderStatusNameTr}
          </span>
        </div>

        {/* Müşteri */}
        <div className="d-flex align-items-center gap-2 mb-2">
          <div
            style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <i className="bi bi-person-fill text-muted" style={{ fontSize: '0.8rem' }}></i>
          </div>
          <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
            {item.preOrder?.customer
              ? `${item.preOrder.customer.customerName} ${item.preOrder.customer.customerSurName}`
              : '—'}
          </span>
        </div>

        {/* Alt satır: tutar + ürün sayısı */}
        <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f3f4f6' }}>
          <span className="fw-bold" style={{ color: '#e67e00', fontSize: '1rem' }}>
            {item.orderPrice} TL
          </span>
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
            <i className="bi bi-bag me-1"></i>
            {item.preOrder?.foodPreOrder?.length ?? 0} ürün
          </span>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, count, color, icon }: { title: string; count: number; color: string; icon: string }) {
  return (
    <div className="d-flex align-items-center mb-3 mt-2">
      <div style={{ width: '5px', height: '28px', backgroundColor: color, borderRadius: '3px', marginRight: '12px', flexShrink: 0 }} />
      <h6 className="mb-0 fw-bold" style={{ color }}>
        <i className={`bi ${icon} me-2`}></i>{title}
      </h6>
      <span className="badge ms-2" style={{ backgroundColor: color, fontSize: '0.75rem', borderRadius: '20px' }}>
        {count}
      </span>
    </div>
  );
}

function OrderListView(props: { OrderList: IOrders }) {
  const [selectedItem, setSelectedItem] = useState<OrderItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('active');

  if (!props.OrderList?.orderList) return <></>;

  const orders = props.OrderList.orderList;

  const activeOrders    = orders.filter(o => { const t = getTaskFromOrder(o); return t !== 1 && t !== 4; });
  const deliveredOrders = orders.filter(o => getTaskFromOrder(o) === 1);
  const cancelledOrders = orders.filter(o => getTaskFromOrder(o) === 4);

  const tabs: { id: TabId; label: string; icon: string; color: string; count?: number }[] = [
    { id: 'active',    label: 'Aktif Siparişler', icon: 'bi-lightning-charge-fill', color: '#f59e0b', count: activeOrders.length    },
    { id: 'delivered', label: 'Teslim Edilenler', icon: 'bi-check-circle-fill',     color: '#22c55e', count: deliveredOrders.length },
    { id: 'cancelled', label: 'İptal Edilenler',  icon: 'bi-x-circle-fill',         color: '#ef4444', count: cancelledOrders.length },
    { id: 'all',       label: 'Tümü',             icon: 'bi-list-ul',               color: '#6366f1', count: orders.length          },
    { id: 'report',    label: 'Rapor',            icon: 'bi-bar-chart-line-fill',   color: '#0ea5e9'                                 },
  ];

  const currentList =
    activeTab === 'active'    ? activeOrders :
    activeTab === 'delivered' ? deliveredOrders :
    activeTab === 'cancelled' ? cancelledOrders :
    orders;

  const currentTab = tabs.find(t => t.id === activeTab)!;

  return (
    <>
      <StandardModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedItem(null); }}
        selectedItem={selectedItem}
      />

      {/* Tab navigation */}
      <div className="d-flex gap-2 flex-wrap mb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="btn d-flex align-items-center gap-2"
            style={{
              borderRadius: '12px',
              fontWeight: 600,
              fontSize: '0.88rem',
              padding: '8px 18px',
              border: `2px solid ${activeTab === tab.id ? tab.color : '#e5e7eb'}`,
              background: activeTab === tab.id ? tab.color : '#fff',
              color: activeTab === tab.id ? '#fff' : '#6b7280',
              boxShadow: activeTab === tab.id ? `0 3px 10px ${tab.color}44` : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            <i className={`bi ${tab.icon}`}></i>
            {tab.label}
            {tab.count !== undefined && (
              <span
                style={{
                  background: activeTab === tab.id ? 'rgba(255,255,255,0.3)' : '#f3f4f6',
                  color: activeTab === tab.id ? '#fff' : '#374151',
                  borderRadius: '20px',
                  padding: '1px 8px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Rapor view */}
      {activeTab === 'report' ? (
        <OrderReport orders={orders} />
      ) : (
        <>
          <SectionHeader
            title={currentTab.label}
            count={currentList.length}
            color={currentTab.color}
            icon={currentTab.icon}
          />

          {currentList.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.3 }}></i>
              <div className="mt-2 small">Bu bölümde sipariş yok.</div>
            </div>
          ) : (
            <div className="row g-2">
              {currentList.map((item, index) => (
                <OrderCard
                  key={item.id || index}
                  item={item}
                  onClick={() => { setSelectedItem(item); setIsModalOpen(true); }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

export default OrderListView;